import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../Utils/baseQueryWithReauth";
import {
  ConsolidatedProductThemeFeedback,
  ConsolidatedTenantContactDataResponse,
  CustomerExecutiveBriefingFeedbackDrilldown,
  CustomerFeedbackSnapshotResponse,
  CVCIQExecutiveOverviewResponse,
  CVCIQProductSnapshot,
  ExecutiveBriefingTenantProductPageResponse,
  ExecutiveBriefingTenantProductRequest,
  ICustomerSummaryResponse,
  IDCRAutomationRequest,
  IDCRAutomationResponse,
  IDCRRequestsResponse,
  IExecutiveBriefing,
  IFeatureDetailsPanelResponse,
  IFeatureEvidenceFilters,
  IOverviewRequest,
  IOverviewResponse,
  IPaginatedEvidenceRequest,
  IPaginatedFeatureRequestResponse,
  IPrioritizeAndDecideRequest,
  IPrioritizeAndDecideResponse,
  IThemesResponse,
  UnifiedInsightsProductEngagementDataResponse,
  UnifiedInsightsUsageMetricsResponse
} from "../interfaces/CloseTheLoop.interfaces";

/**
 * Helper to build query params for paginated requests
 */
const buildPaginatedQueryParams = (params: IPaginatedEvidenceRequest): string => {
  const searchParams = new URLSearchParams();

  // Add pagination params
  if (params.page) searchParams.append("page", params.page.toString());
  if (params.pageSize) searchParams.append("pageSize", params.pageSize.toString());
  if (params.sortBy) searchParams.append("sortBy", params.sortBy);
  if (params.sortDirection) searchParams.append("sortDirection", params.sortDirection);

  // Add global search term
  if (params.searchTerm) searchParams.append("searchTerm", params.searchTerm);

  // Add text search filters
  if (params.featureRequestIdContains) searchParams.append("featureRequestIdContains", params.featureRequestIdContains);
  if (params.featureEvidenceIdContains)
    searchParams.append("featureEvidenceIdContains", params.featureEvidenceIdContains);
  if (params.productContains) searchParams.append("productContains", params.productContains);
  if (params.titleContains) searchParams.append("titleContains", params.titleContains);
  if (params.customerContains) searchParams.append("customerContains", params.customerContains);
  if (params.tenantNameContains) searchParams.append("tenantNameContains", params.tenantNameContains);
  if (params.tenantIdContains) searchParams.append("tenantIdContains", params.tenantIdContains);
  if (params.submitterContains) searchParams.append("submitterContains", params.submitterContains);
  if (params.tagContains) searchParams.append("tagContains", params.tagContains);
  if (params.caseNumberContains) searchParams.append("caseNumberContains", params.caseNumberContains);

  // Add dropdown filters
  if (params.statuses) searchParams.append("statuses", params.statuses);
  if (params.priorities) searchParams.append("priorities", params.priorities);
  if (params.products) searchParams.append("products", params.products);
  if (params.submitters) searchParams.append("submitters", params.submitters);
  if (params.stages) searchParams.append("stages", params.stages);
  if (params.etas) searchParams.append("etas", params.etas);
  if (params.industries) searchParams.append("industries", params.industries);
  if (params.verticals) searchParams.append("verticals", params.verticals);
  if (params.regions) searchParams.append("regions", params.regions);
  if (params.sources) searchParams.append("sources", params.sources);
  if (params.featureCategories) searchParams.append("featureCategories", params.featureCategories);
  if (params.caseNumbers) searchParams.append("caseNumbers", params.caseNumbers);

  // Add date range filters
  if (params.createdDateFrom) searchParams.append("createdDateFrom", params.createdDateFrom);
  if (params.createdDateTo) searchParams.append("createdDateTo", params.createdDateTo);
  if (params.lastChangedDateFrom) searchParams.append("lastChangedDateFrom", params.lastChangedDateFrom);
  if (params.lastChangedDateTo) searchParams.append("lastChangedDateTo", params.lastChangedDateTo);

  return searchParams.toString();
};

export const closeTheLoopAPI = createApi({
  reducerPath: "closeTheLoop",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["CloseTheLoopData", "FeatureDetails", "Filters", "Customers", "FeatureRequests", "DCRRequests"],
  // Keep data cached for 30 minutes to avoid refetching large dataset
  keepUnusedDataFor: 1800,
  // Don't refetch when window regains focus
  refetchOnFocus: false,
  // Don't refetch on reconnect (data is cached on backend too)
  refetchOnReconnect: false,
  endpoints: (builder) => ({
    getExecutiveSummaryAccess: builder.query<{ canAccessExecutiveSummary: boolean }, void>({
      query: () => "user/hasExecutiveSummaryAccess"
    }),
    /**
     * Get paginated feature requests (grouped by feature request ID)
     * Shows one row per feature request with evidence and customer counts
     */
    getPaginatedFeatureRequests: builder.query<IPaginatedFeatureRequestResponse, IPaginatedEvidenceRequest>({
      query: (params) => {
        const queryString = buildPaginatedQueryParams(params);
        return `closetheloop/feature-requests${queryString ? `?${queryString}` : ""}`;
      },
      providesTags: ["FeatureRequests"]
    }),

    /**
     * Get only filter options (for initial load without data, fast response)
     */
    getFilters: builder.query<IFeatureEvidenceFilters, void>({
      query: () => "closetheloop/filters",
      providesTags: ["Filters"]
    }),

    /**
     * Get customer summaries only (lightweight, for Customer View tab)
     */
    getCustomers: builder.query<ICustomerSummaryResponse, void>({
      query: () => "closetheloop/customers",
      providesTags: ["Customers"]
    }),

    /**
     * Get complete panel details including evidence, lifecycle, and mapping
     */
    getFeatureDetails: builder.query<IFeatureDetailsPanelResponse, string>({
      query: (featureRequestIdWithSource) => `closetheloop/details/${encodeURIComponent(featureRequestIdWithSource)}`,
      providesTags: (_result, _error, id) => [{ type: "FeatureDetails", id }]
    }),

    /**
     * Get overview data for Review & Analyze Overview tab
     * Includes executive summary, metrics, charts data
     */
    getOverview: builder.query<IOverviewResponse, IOverviewRequest | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.products) searchParams.append("products", params.products);
        if (params?.productFamilies) searchParams.append("productFamilies", params.productFamilies);
        if (params?.dateRange) searchParams.append("dateRange", params.dateRange);
        if (params?.startDate) searchParams.append("startDate", params.startDate);
        if (params?.endDate) searchParams.append("endDate", params.endDate);
        const queryString = searchParams.toString();
        return `closetheloop/overview${queryString ? `?${queryString}` : ""}`;
      },
      providesTags: ["CloseTheLoopData"]
    }),

    /**
     * Get themes with feature evidences for Top Themes section
     * Returns aggregated theme summaries with trend analysis
     */
    getThemes: builder.query<IThemesResponse, void>({
      query: () => "closetheloop/themes",
      providesTags: ["CloseTheLoopData"]
    }),

    /**
     * Get DCR Automation metrics for Intake to Triage tab
     * Includes summary metrics, failure reasons, conversion charts, and reconciliation data
     */
    getDCRAutomation: builder.query<IDCRAutomationResponse, IDCRAutomationRequest | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.offerings) searchParams.append("offerings", params.offerings);
        if (params?.azureServices) searchParams.append("azureServices", params.azureServices);
        if (params?.categories) searchParams.append("categories", params.categories);
        if (params?.failureReasons) searchParams.append("failureReasons", params.failureReasons);
        if (params?.searchTerm) searchParams.append("searchTerm", params.searchTerm);
        const queryString = searchParams.toString();
        return `closetheloop/dcr-automation${queryString ? `?${queryString}` : ""}`;
      },
      providesTags: ["CloseTheLoopData"]
    }),

    getDCRRequests: builder.query<IDCRRequestsResponse, void>({
      query: () => "closetheloop/dcr-requests",
      providesTags: ["DCRRequests"]
    }),

    /**
     * Get Prioritize & Decide metrics for the Prioritize & Decide tab
     * Includes overview KPIs, top requests, delivery pipeline, backlog alignment,
     * active-in-engineering, time-to-deliver, and distribution charts
     */
    getPrioritizeAndDecide: builder.query<IPrioritizeAndDecideResponse, IPrioritizeAndDecideRequest | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.productFamilies) searchParams.append("productFamilies", params.productFamilies);
        if (params?.priorities) searchParams.append("priorities", params.priorities);
        if (params?.statuses) searchParams.append("statuses", params.statuses);
        if (params?.searchTerm) searchParams.append("searchTerm", params.searchTerm);
        const queryString = searchParams.toString();
        return `closetheloop/prioritize-decide${queryString ? `?${queryString}` : ""}`;
      },
      providesTags: ["CloseTheLoopData"]
    }),

    getExecutiveBriefing: builder.query<IExecutiveBriefing[], void>({
      query: () => "closetheloop/executive-briefing",
      transformResponse: (response: IExecutiveBriefing[] | IExecutiveBriefing) =>
        Array.isArray(response) ? response : [response],
      providesTags: ["CloseTheLoopData"]
    }),

    getExecutiveBriefingV1: builder.query<IExecutiveBriefing[], string>({
      query: (tenantId) => `closetheloop/executive-briefing-v1?tenantId=${encodeURIComponent(tenantId)}`,
      transformResponse: (response: IExecutiveBriefing[] | IExecutiveBriefing | null | undefined) =>
        response ? (Array.isArray(response) ? response : [response]) : [],
      providesTags: ["CloseTheLoopData"]
    }),
    getCVCIQExecutiveOverview: builder.query<
      CVCIQExecutiveOverviewResponse,
      { tenantId: string; productName: string; feedbackPeriod: string }
    >({
      query: ({ tenantId, productName, feedbackPeriod }) =>
        `closetheloop/getCVCIQExecutiveOverview?tenantId=${encodeURIComponent(
          tenantId
        )}&productName=${encodeURIComponent(productName)}&feedbackPeriod=${encodeURIComponent(feedbackPeriod)}`,
      providesTags: ["CloseTheLoopData"]
    }),
    getCVCIQProductSnapshot: builder.query<
      CVCIQProductSnapshot[],
      { tenantId: string; productName: string; feedbackPeriod: string }
    >({
      query: ({ tenantId, productName, feedbackPeriod }) =>
        `closetheloop/getCVCIQProductSnapshot?tenantId=${encodeURIComponent(
          tenantId
        )}&productName=${encodeURIComponent(productName)}&feedbackPeriod=${encodeURIComponent(feedbackPeriod)}`,
      providesTags: ["CloseTheLoopData"]
    }),
    /**
     * Prefetch panel details for multiple feature requests
     * Called after loading list to pre-warm cache for faster panel loading
     */
    prefetchPanels: builder.mutation<{ prefetched: number; total: number }, string[]>({
      query: (featureRequestIds) => ({
        url: "closetheloop/prefetch-panels",
        method: "POST",
        body: featureRequestIds
      })
    }),
    getUnifiedInsightsProductEngagementData: builder.query<UnifiedInsightsProductEngagementDataResponse[], string>({
      query: (dashboardSection) =>
        `closetheloop/getUnifiedInsightsProductEngagementData?dashboardSection=${dashboardSection}`
    }),
    getUnifiedInsightsUsageMetrics: builder.query<UnifiedInsightsUsageMetricsResponse[], void>({
      query: () => "closetheloop/getUnifiedInsightsProductEngagementData?dashboardSection=UsageMetrics"
    }),
    getConsolidatedTenantContactsData: builder.query<ConsolidatedTenantContactDataResponse[], string>({
      query: (tenantId) => `closetheloop/consolidated-tenant-contacts?tenantId=${tenantId}`
    }),
    getUnifiedInsightsTopCustomerNeedsDrilldownData: builder.query<
      ConsolidatedProductThemeFeedback[],
      { productName: string; feedbackPeriod: string; sourceLabel: string }
    >({
      query: ({ productName, feedbackPeriod, sourceLabel }) =>
        `closetheloop/top-customer-needs?productName=${productName}&feedbackPeriod=${feedbackPeriod}&sourceLabel=${sourceLabel}`
    }),
    getCustomerEngagementData: builder.query<
      CustomerFeedbackSnapshotResponse[],
      { productName: string; feedbackPeriod: string }
    >({
      query: ({ productName, feedbackPeriod }) =>
        `closetheloop/customer-engagement-data?productName=${productName}&feedbackPeriod=${feedbackPeriod}`
    }),
    getExecutiveBriefingTenantProducts: builder.query<
      ExecutiveBriefingTenantProductPageResponse,
      ExecutiveBriefingTenantProductRequest | void
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.search) searchParams.append("search", params.search);
        if (params?.cursor) searchParams.append("cursor", params.cursor);
        if (params?.take !== undefined) searchParams.append("take", params.take.toString());
        const queryString = searchParams.toString();
        return `closetheloop/executive-briefing-tenant-products${queryString ? `?${queryString}` : ""}`;
      },
      providesTags: ["CloseTheLoopData"]
    }),
    getExecutiveBriefingFeedbackDrilldownData: builder.query<
      CustomerExecutiveBriefingFeedbackDrilldown[],
      { tenantId: string; productName: string; feedbackPeriod: string }
    >({
      query: ({ tenantId, productName, feedbackPeriod }) =>
        `closetheloop/executive-briefing-feedback-drilldown?tenantId=${encodeURIComponent(
          tenantId
        )}&productName=${encodeURIComponent(productName)}&feedbackPeriod=${encodeURIComponent(feedbackPeriod)}`
    })
  })
});

export const {
  useGetExecutiveSummaryAccessQuery,
  useGetPaginatedFeatureRequestsQuery,
  useLazyGetPaginatedFeatureRequestsQuery,
  useGetFiltersQuery,
  useGetCustomersQuery,
  useLazyGetCustomersQuery,
  useGetFeatureDetailsQuery,
  useGetOverviewQuery,
  useGetThemesQuery,
  useGetDCRAutomationQuery,
  useGetDCRRequestsQuery,
  useGetPrioritizeAndDecideQuery,
  useGetExecutiveBriefingQuery,
  useGetExecutiveBriefingV1Query,
  useGetCVCIQExecutiveOverviewQuery,
  useGetCVCIQProductSnapshotQuery,
  usePrefetchPanelsMutation,
  useGetUnifiedInsightsProductEngagementDataQuery,
  useGetUnifiedInsightsUsageMetricsQuery,
  useGetConsolidatedTenantContactsDataQuery,
  useGetUnifiedInsightsTopCustomerNeedsDrilldownDataQuery,
  useGetCustomerEngagementDataQuery,
  useGetExecutiveBriefingTenantProductsQuery,
  useLazyGetExecutiveBriefingTenantProductsQuery,
  useGetExecutiveBriefingFeedbackDrilldownDataQuery
} = closeTheLoopAPI;

// Export prefetch utilities for background data loading
export const { usePrefetch: useCloseTheLoopPrefetch, endpoints: closeTheLoopEndpoints } = closeTheLoopAPI;
